// Sidebar.js
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faBars } from "@fortawesome/free-solid-svg-icons";
import { colors } from "../../devdata/constants/themes";
import expandedLogo from "../../devdata/icons/marketpulse.svg";
import collapsedLogo from "../../devdata/icons/s_logo.svg";

const Sidebar = ({
  menuItemsTop = [],
  menuItemsBottom = [],
  onMenuItemClick = () => {},
  isCollapsed,
  onChange,
  isMobileView,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isResponsiveCollapsed, setIsResponsiveCollapsed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isCollapsed || isResponsiveCollapsed || isMobileMenuOpen) {
      setShouldRender(true);
    } else {
      setTimeout(() => setShouldRender(false), 200);
    }
  }, [isCollapsed, isResponsiveCollapsed, isMobileMenuOpen]);

  const toggleSidebar = () => {
    if (!isMobileView) {
      onChange((prev) => !prev);
    } else {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    }
  };

  return (
    <div
      className={`bg-darkMode-background shadow-md min-h-screen border-r border-0.5 border-darkMode-layer3 transition-all duration-300  max-md:z-50 ${
        isMobileView
          ? "fixed inset-y-0 left-0 h-screen" // Fixed positioning for mobile overlay
          : "absolute sticky inset-0 top" // Absolute positioning for desktop sticky
      } ${
        isMobileView
          ? isMobileMenuOpen
            ? "w-[200px] overflow-hidden" // Removed pt-[80px] for mobile
            : "w-0 border-none overflow-hidden"
          : isCollapsed
          ? "w-[72px]"
          : "w-[200px]" // Increased desktop width from 176px to 200px
      } ${
        isMobileView
          ? isMobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full" // Mobile slide animation
          : "md:translate-x-0" // No translation on desktop
      } md:translate-x-0 z-50`} // Added z-50 to Sidebar for overlay
    >
      {/* Toggle Button for Desktop */}
      {!isMobileView && (
        <button
          onClick={toggleSidebar}
          className={`absolute top-1/2 -right-4 bg-darkMode-background border border-gray-500 px-1.5 py-1.5 rounded-full shadow-lg group hover:bg-gray-200 transition-all duration-200 md:flex hidden z-high ${
            isCollapsed || isResponsiveCollapsed ? "rotate-180" : ""
          }`}
          aria-label="Toggle Sidebar"
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="text-darkMode-text group-hover:text-black h-5 w-5"
          />
        </button>
      )}

      {/* Sidebar Menu */}
      <div
        className={`flex flex-col min-h-screen justify-between py-[32px] px-[6px] 
          ${isMobileView ? "" : "overflow-hidden"}
          `}
      >
        {/* Logo */}
        <div
          className={`flex flex-col gap-3 ${
            shouldRender && !isMobileView
              ? "items-center"
              : "items-start" /* items-center only for desktop collapsed */
          }`}
        >
          <div
            className={`${
              shouldRender && !isMobileView ? "w-[32px]" : "w-[180px]"
            } mb-10 transition-all duration-300 flex items-center justify-center`}
          >
            <img
              src={
                shouldRender
                  ? isMobileMenuOpen
                    ? expandedLogo
                    : collapsedLogo
                  : expandedLogo
              }
              alt="Logo"
              className={`h-auto transition-all duration-300 ${
                shouldRender && !isMobileView ? "w-[32px]" : "w-[180px] ml-2"
              }`}
            />
          </div>

          {menuItemsTop.map((item, index) => (
            <Link to={item.path} key={index} className="block w-full">
              <div
                className={`flex items-center gap-3 py-2 px-[6px] cursor-pointer rounded-md hover:bg-darkMode-layer2 transition w-full ${
                  /* Added 'w-full' here */
                  location.pathname === item.path ? "bg-darkMode-layer2" : ""
                } ${
                  shouldRender && !isMobileView ? "w-[48px] justify-center" : ""
                }`}
                onClick={() => onMenuItemClick(item.label)}
              >
                <item.icon
                  fill={
                    location.pathname === item.path
                      ? colors.darkMode.green
                      : colors.darkMode.text
                  }
                />
                {!(shouldRender && !isMobileView) && (
                  <span
                    className="text-md font-medium text-darkMode-text"
                    style={{
                      color:
                        location.pathname === item.path
                          ? colors.darkMode.green
                          : colors.darkMode.text,
                    }}
                  >
                    {item.label}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
        <div
          className={`flex flex-col gap-3 ${
            shouldRender && !isMobileView ? "items-center" : "items-start"
          }`}
        >
          {menuItemsBottom.map((item, index) => (
            <Link to={item.path} key={index} className="block w-full">
              <div
                className={`flex items-center gap-3 py-2 px-[6px] cursor-pointer rounded-md hover:bg-darkMode-layer2 transition ${
                  location.pathname === item.path ? "bg-darkMode-layer2" : ""
                } ${
                  shouldRender && !isMobileView ? "w-[48px] justify-center" : ""
                }`}
                onClick={() => onMenuItemClick(item.label)}
              >
                <item.icon
                  fill={
                    location.pathname === item.path
                      ? colors.darkMode.green
                      : colors.darkMode.text
                  }
                />
                {!(shouldRender && !isMobileView) && (
                  <span
                    style={{
                      color:
                        location.pathname === item.path
                          ? colors.darkMode.green
                          : colors.darkMode.text,
                    }}
                    className="text-md font-medium text-darkMode-text"
                  >
                    {item.label}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
